import type { TemplateExport } from '@/modules/admin/templates/lib/export-import-schema';
import { importTemplate } from '@/modules/admin/templates/lib/export-import.service';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle2, FileJson, Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface ValidationError {
  path: (string | number)[];
  message: string;
}

export function ImportDialog({ open, onOpenChange, onSuccess }: ImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TemplateExport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setParseError(null);
    setImportError(null);
    setValidationErrors([]);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  const processFile = async (file: File) => {
    setFile(file);
    setParseError(null);
    setImportError(null);
    setValidationErrors([]);

    try {
      const content = await file.text();
      const parsed = JSON.parse(content) as TemplateExport;

      // Basic structure validation
      if (!parsed.version || !parsed.template) {
        setParseError('Invalid export format. Missing required fields.');
        setPreview(null);
        return;
      }

      setPreview(parsed);
    } catch {
      setParseError('Failed to parse JSON file. Please check the file format.');
      setPreview(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    processFile(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.type === 'application/json' || droppedFile?.name.endsWith('.json')) {
      processFile(droppedFile);
    } else {
      setParseError('Please drop a JSON file.');
    }
  };

  const handleImport = async () => {
    if (!preview) return;

    setIsImporting(true);
    setImportError(null);
    setValidationErrors([]);

    try {
      const result = await importTemplate(preview);

      if (!result.success) {
        setImportError(result.message);
        return;
      }

      onSuccess();
      handleOpenChange(false);
    } catch (err: any) {
      if (err?.issues) {
        setValidationErrors(
          err.issues.map((issue: any) => ({ path: issue.path, message: issue.message }))
        );
        setImportError('The imported JSON does not match the expected format.');
      } else {
        setImportError('An unexpected error occurred while importing.');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const screenCount = preview?.template.screens.length ?? 0;
  const fieldCount =
    preview?.template.screens.reduce((acc, screen) => acc + screen.fields.length, 0) ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3 pr-8">
            <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-full bg-selise-blue/10">
              <Upload className="size-4 sm:size-5 text-selise-blue" />
            </div>
            <DialogTitle className="text-base sm:text-lg">Import Template</DialogTitle>
          </div>
          <DialogDescription className="text-xs sm:text-sm">
            Import a template from a JSON file. The file should follow the export format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4">
          {/* File Input */}
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-4 sm:p-6 text-center cursor-pointer transition-colors',
              isDragging
                ? 'border-selise-blue bg-selise-blue/5'
                : 'border-border hover:border-selise-blue/50'
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-[hsl(var(--fg))] min-w-0">
                <FileJson className="size-5 shrink-0 text-selise-blue" />
                <span className="font-medium truncate">{file.name}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="size-7 sm:size-8 mx-auto text-globe-grey" />
                <p className="text-xs sm:text-sm text-globe-grey">
                  {isDragging
                    ? 'Drop JSON file here'
                    : 'Drag & drop or click to select a JSON file'}
                </p>
              </div>
            )}
          </div>

          {/* Parse Error */}
          {parseError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
              <AlertCircle className="size-5 shrink-0 mt-0.5" />
              <p className="text-sm">{parseError}</p>
            </div>
          )}

          {/* Preview */}
          {preview && !parseError && (
            <div className="space-y-3 p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-lime-green" />
                <span className="font-medium text-[hsl(var(--fg))]">Valid template file</span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-globe-grey">Title</span>
                  <span className="font-medium text-[hsl(var(--fg))]">
                    {preview.template.title}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-globe-grey">Slug</span>
                  <code className="bg-[hsl(var(--bg))] px-2 py-0.5 rounded text-[hsl(var(--fg))]">
                    {preview.template.slug}
                  </code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-globe-grey">Screens</span>
                  <Badge variant="secondary">{screenCount}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-globe-grey">Fields</span>
                  <Badge variant="secondary">{fieldCount}</Badge>
                </div>
                {preview.template.available && (
                  <div className="flex items-center justify-between">
                    <span className="text-globe-grey">Status</span>
                    <Badge className="bg-lime-green text-white">Available</Badge>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-globe-grey">Exported</span>
                  <span className="text-[hsl(var(--fg))]">
                    {new Date(preview.exportedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
              <AlertCircle className="size-5 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{importError}</p>
                {validationErrors.length > 0 && (
                  <ul className="text-xs space-y-0.5 list-disc list-inside">
                    {validationErrors.slice(0, 5).map((err, i) => (
                      <li key={i}>
                        {err.path.join('.')}: {err.message}
                      </li>
                    ))}
                    {validationErrors.length > 5 && (
                      <li>...and {validationErrors.length - 5} more errors</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button
            variant="outline"
            size={'sm'}
            onClick={() => handleOpenChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            size={'sm'}
            onClick={handleImport}
            disabled={!preview || isImporting || !!parseError}
          >
            {isImporting && <Loader2 className="size-4 animate-spin mr-2" />}
            Import Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
